import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Check, Loader2, X } from "lucide-react";

const timeAgo = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export const NotificationBell = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [respondingId, setRespondingId] = useState(null);
  const [resolved, setResolved] = useState({}); // contract_id -> approved|declined

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, [user, poll]);

  const onOpenChange = (o) => {
    setOpen(o);
    if (o && unread > 0) {
      api.post("/notifications/mark-read", {}).then(() => setUnread(0)).catch(() => {});
    }
    // Sync upcharge contract states so resolved requests don't show action buttons
    if (o && ["model", "agency"].includes(user?.role)) {
      api.get("/upcharge").then(({ data }) => {
        const map = {};
        data.forEach((c) => { if (c.status !== "pending") map[c.id] = c.status; });
        setResolved((r) => ({ ...map, ...r }));
      }).catch(() => {});
    }
  };

  const respond = async (notif, action) => {
    const contractId = notif.data?.contract_id;
    if (!contractId) return;
    setRespondingId(contractId);
    try {
      await api.post(`/upcharge/${contractId}/respond`, { action });
      setResolved((r) => ({ ...r, [contractId]: action === "approve" ? "approved" : "declined" }));
      toast.success(action === "approve"
        ? `Upgrade approved — ${fmtMoney(notif.data?.amount || 0)} released to your wallet`
        : "Upgrade declined — the brand was refunded in full");
      poll();
    } catch (e) {
      toast.error(errMsg(e, "Could not respond — it may already be resolved"));
      setResolved((r) => ({ ...r, [contractId]: "resolved" }));
    } finally {
      setRespondingId(null);
    }
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell-button"
          aria-label="Notifications"
          className="relative ml-1 flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-[background-color,color] hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Bell className="h-[18px] w-[18px]" />
          {unread > 0 && (
            <span data-testid="notification-unread-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(6,62%,45%)] px-1 text-[9px] font-bold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 rounded-2xl p-0">
        <div className="border-b px-4 py-3">
          <p className="font-display text-lg">Notifications</p>
        </div>
        <div data-testid="notification-list" className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">Nothing yet — twin activity, approvals and payouts land here.</p>
          ) : (
            items.map((n) => {
              const contractId = n.data?.contract_id;
              const isPendingUpcharge = n.type === "upcharge_request" && contractId && !resolved[contractId];
              return (
                <div key={n.id} data-testid="notification-item" className={`border-b px-4 py-3 last:border-b-0 ${!n.read ? "bg-[hsl(174,38%,97%)]" : ""}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold">{n.title}</p>
                    <span className="shrink-0 font-mono-plex text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                  </div>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{n.body}</p>
                  {isPendingUpcharge && ["model", "agency"].includes(user.role) && (
                    <div className="mt-2 flex gap-2">
                      <Button data-testid="notification-upcharge-approve" size="sm" disabled={respondingId === contractId}
                        onClick={() => respond(n, "approve")} className="h-7 rounded-lg text-xs">
                        {respondingId === contractId ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />} Approve
                      </Button>
                      <Button data-testid="notification-upcharge-decline" size="sm" variant="outline" disabled={respondingId === contractId}
                        onClick={() => respond(n, "decline")} className="h-7 rounded-lg border bg-card text-xs text-destructive hover:bg-muted">
                        <X className="mr-1 h-3 w-3" /> Decline
                      </Button>
                    </div>
                  )}
                  {contractId && resolved[contractId] && (
                    <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-wide text-[hsl(174,38%,30%)]">{resolved[contractId]}</p>
                  )}
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
